import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';

import { Save, Trash2, ArrowLeft, Loader2, Plus, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { getProduct, saveProduct, deleteProduct } from '../../../services/firestore/formularyService';
import type { Product, ProductCategory, ProductSubcategory, ProductStatus, ProductRoute, ProductTrigger, ProductPricing } from '../../../types/formulary';
import type { ClinicalTier, ProtocolTiming, HealthGoal } from '../../../types/index';

const EMPTY_PRODUCT: Product = {
  id: '',
  catalogNumber: 0,
  name: '',
  ingredients: '',
  category: 'supplement',
  subcategory: 'foundation',
  tier: 1,
  route: 'oral',
  dosage: '',
  timing: 'AM_ENERGY',
  additionalTimings: [],
  triggers: [],
  targetBiomarkers: [],
  targetGoals: [],
  pairsWith: [],
  contraindications: [],
  drugInteractions: [],
  allergenFlags: [],
  pregnancyCategory: '',
  requiresRx: false,
  requiresProvider: false,
  monitoringRequirements: [],
  evidenceLevel: 'moderate',
  evidenceSummary: '',
  citations: [],
  gender: 'any',
  ageMin: undefined,
  ageMax: undefined,
  clinicalNotes: '',
  rationale: '',
  pricing: { manufacturingCost: '', supplierCost: '', monthlyPrice: '' },
  status: 'draft',
  sortOrder: 0,
  updatedAt: '',
  updatedBy: 'admin',
};

const CATEGORIES: ProductCategory[] = ['supplement', 'rx', 'longevity'];
const SUBCATEGORIES: ProductSubcategory[] = [
  'foundation', 'beauty', 'weight_metabolic', 'hormone_female', 'hormone_male',
  'clinical', 'hrt_female', 'hrt_male', 'thyroid', 'glp1_weight', 'skin_hair',
  'mental_health', 'sexual_female', 'sexual_male', 'lipid', 'nad_cellular',
  'gh_peptide', 'mtor_longevity', 'metabolic_longevity', 'immune_inflammation',
  'mitochondrial_cognitive', 'neurohormone', 'hormone_precursor', 'methylation_energy',
  'antioxidant_detox', 'joint_musculoskeletal', 'skin_regeneration',
];
const STATUSES: ProductStatus[] = ['active', 'draft', 'discontinued'];
const ROUTES: ProductRoute[] = ['oral', 'topical', 'sublingual', 'transdermal', 'subq', 'im', 'iv', 'vaginal', 'nasal', 'patch'];
const TIMINGS: ProtocolTiming[] = ['AM_ENERGY', 'MIDDAY_ABSORPTION', 'PM_RECOVERY'];
const EVIDENCE_LEVELS: Array<'strong' | 'moderate' | 'emerging'> = ['strong', 'moderate', 'emerging'];
const GENDERS: Array<'female' | 'male' | 'any'> = ['female', 'male', 'any'];
const HEALTH_GOALS: HealthGoal[] = [
  'energy', 'sleep', 'cognition', 'longevity', 'body_composition',
  'hormonal', 'cardiovascular', 'immune', 'sexual_health', 'prostate',
];
const TRIGGER_CONDITIONS: ProductTrigger['condition'][] = [
  'below_functional', 'below_standard', 'above_functional', 'above_standard', 'any_suboptimal',
];

export function ProductForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const [product, setProduct] = useState<Product>({ ...EMPTY_PRODUCT });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['basic', 'dosage', 'triggers', 'safety', 'evidence', 'demographics', 'pricing', 'meta'])
  );

  useEffect(() => {
    if (id) {
      loadProduct(id);
    }
  }, [id]);

  async function loadProduct(productId: string) {
    try {
      setLoading(true);
      const data = await getProduct(productId);
      if (data) {
        setProduct(data);
      } else {
        setToast('Product not found');
        navigate('/admin/products');
      }
    } catch (err) {
      setToast('Error loading product: ' + (err instanceof Error ? err.message : 'Unknown'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!product.id.trim()) {
      setToast('Product ID is required');
      return;
    }
    if (!product.name.trim()) {
      setToast('Product name is required');
      return;
    }
    try {
      setSaving(true);
      await saveProduct(product);
      setToast('Product saved successfully');
      if (isNew) {
        navigate(`/admin/products/${product.id}`, { replace: true });
      }
    } catch (err) {
      setToast('Error saving: ' + (err instanceof Error ? err.message : 'Unknown'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!id) return;
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    try {
      setDeleting(true);
      await deleteProduct(id);
      setToast('Product deleted');
      navigate('/admin/products');
    } catch (err) {
      setToast('Error deleting: ' + (err instanceof Error ? err.message : 'Unknown'));
      setDeleting(false);
    }
  }

  function toggleSection(section: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }

  function updateField<K extends keyof Product>(key: K, value: Product[K]) {
    setProduct((prev) => ({ ...prev, [key]: value }));
  }

  function updatePricing<K extends keyof ProductPricing>(key: K, value: string) {
    setProduct((prev) => ({
      ...prev,
      pricing: { ...prev.pricing, [key]: value },
    }));
  }

  // Parse comma-separated string into array
  function parseArray(value: string): string[] {
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }

  // Triggers management
  function addTrigger() {
    setProduct((prev) => ({
      ...prev,
      triggers: [...prev.triggers, { biomarker: '', condition: 'below_functional', threshold: undefined, description: '' }],
    }));
  }

  function updateTrigger(index: number, field: keyof ProductTrigger, value: string | number | undefined) {
    setProduct((prev) => {
      const triggers = [...prev.triggers];
      triggers[index] = { ...triggers[index], [field]: value };
      return { ...prev, triggers };
    });
  }

  function removeTrigger(index: number) {
    setProduct((prev) => ({
      ...prev,
      triggers: prev.triggers.filter((_, i) => i !== index),
    }));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-4 right-4 z-50 bg-bg-elevated border border-bg-tertiary rounded-lg px-4 py-3 text-base shadow-lg flex items-center gap-2"
        >
          <Check size={16} className="text-optimal" />
          {toast}
          <button onClick={() => setToast(null)} className="ml-2 text-text-muted hover:text-text-primary">
            <X size={14} />
          </button>
        </motion.div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/products')} className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-3xl font-display text-gold">{isNew ? 'New Product' : 'Edit Product'}</h2>
            {!isNew && <p className="text-base text-text-muted">ID: {product.id}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-base font-medium transition-colors disabled:opacity-50"
            >
              {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              Delete
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Product
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Basic Info */}
        <FormSection title="Basic Info" id="basic" expanded={expandedSections.has('basic')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Product ID" required>
              <input
                type="text"
                value={product.id}
                onChange={(e) => updateField('id', e.target.value)}
                disabled={!isNew}
                placeholder="e.g. foundation-multi-vitamin"
                className="input-field"
              />
            </Field>
            <Field label="Catalog Number">
              <input
                type="number"
                value={product.catalogNumber}
                onChange={(e) => updateField('catalogNumber', Number(e.target.value))}
                className="input-field"
              />
            </Field>
            <Field label="Name" required className="sm:col-span-2">
              <input
                type="text"
                value={product.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Product name"
                className="input-field"
              />
            </Field>
            <Field label="Ingredients" className="sm:col-span-2">
              <textarea
                value={product.ingredients || ''}
                onChange={(e) => updateField('ingredients', e.target.value)}
                placeholder="Active ingredients..."
                rows={2}
                className="input-field"
              />
            </Field>
            <Field label="Category">
              <select value={product.category} onChange={(e) => updateField('category', e.target.value as ProductCategory)} className="input-field">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Subcategory">
              <select value={product.subcategory} onChange={(e) => updateField('subcategory', e.target.value as ProductSubcategory)} className="input-field">
                {SUBCATEGORIES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </Field>
            <Field label="Tier">
              <select value={product.tier} onChange={(e) => updateField('tier', Number(e.target.value) as ClinicalTier)} className="input-field">
                <option value={1}>Tier 1 - Foundation</option>
                <option value={2}>Tier 2 - Rx</option>
                <option value={3}>Tier 3 - Longevity</option>
                <option value={4}>Tier 4 - Advanced</option>
              </select>
            </Field>
            <Field label="Status">
              <select value={product.status} onChange={(e) => updateField('status', e.target.value as ProductStatus)} className="input-field">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
        </FormSection>

        {/* Dosage & Timing */}
        <FormSection title="Dosage & Timing" id="dosage" expanded={expandedSections.has('dosage')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Route">
              <select value={product.route} onChange={(e) => updateField('route', e.target.value as ProductRoute)} className="input-field">
                {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Dosage">
              <input
                type="text"
                value={product.dosage}
                onChange={(e) => updateField('dosage', e.target.value)}
                placeholder="e.g. 2 caps daily (AM w/food)"
                className="input-field"
              />
            </Field>
            <Field label="Primary Timing">
              <select value={product.timing} onChange={(e) => updateField('timing', e.target.value as ProtocolTiming)} className="input-field">
                {TIMINGS.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </Field>
            <Field label="Additional Timings (comma-separated)">
              <input
                type="text"
                value={(product.additionalTimings || []).join(', ')}
                onChange={(e) => updateField('additionalTimings', parseArray(e.target.value) as ProtocolTiming[])}
                placeholder="e.g. PM_RECOVERY"
                className="input-field"
              />
            </Field>
          </div>
        </FormSection>

        {/* Clinical Triggers */}
        <FormSection title="Clinical Triggers" id="triggers" expanded={expandedSections.has('triggers')} onToggle={toggleSection}>
          <div className="space-y-4">
            <Field label="Target Biomarkers (comma-separated)">
              <input
                type="text"
                value={product.targetBiomarkers.join(', ')}
                onChange={(e) => updateField('targetBiomarkers', parseArray(e.target.value))}
                placeholder="e.g. Vitamin D, Ferritin, B12"
                className="input-field"
              />
            </Field>
            <Field label="Target Goals">
              <div className="flex flex-wrap gap-2">
                {HEALTH_GOALS.map((goal) => (
                  <button
                    key={goal}
                    type="button"
                    onClick={() => {
                      const goals = product.targetGoals.includes(goal)
                        ? product.targetGoals.filter((g) => g !== goal)
                        : [...product.targetGoals, goal];
                      updateField('targetGoals', goals);
                    }}
                    className={`px-2.5 py-1 rounded text-sm font-medium border transition-colors ${
                      product.targetGoals.includes(goal)
                        ? 'border-gold/50 text-gold bg-gold/10'
                        : 'border-bg-tertiary text-text-muted hover:text-text-secondary'
                    }`}
                  >
                    {goal.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Pairs With (product IDs, comma-separated)">
              <input
                type="text"
                value={product.pairsWith.join(', ')}
                onChange={(e) => updateField('pairsWith', parseArray(e.target.value))}
                placeholder="e.g. omega-3-fish-oil, magnesium-glycinate"
                className="input-field"
              />
            </Field>

            {/* Trigger conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-base font-medium text-text-secondary">Trigger Conditions</label>
                <button
                  type="button"
                  onClick={addTrigger}
                  className="inline-flex items-center gap-1 text-sm text-gold hover:text-gold/80"
                >
                  <Plus size={14} /> Add Trigger
                </button>
              </div>
              {product.triggers.length === 0 && (
                <p className="text-sm text-text-muted">No triggers defined.</p>
              )}
              <div className="space-y-3">
                {product.triggers.map((trigger, idx) => (
                  <div key={idx} className="bg-bg-elevated border border-bg-tertiary rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="text-sm text-text-muted">Trigger #{idx + 1}</span>
                      <button onClick={() => removeTrigger(idx)} className="text-text-muted hover:text-critical">
                        <X size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={trigger.biomarker}
                        onChange={(e) => updateTrigger(idx, 'biomarker', e.target.value)}
                        placeholder="Biomarker"
                        className="input-field text-sm"
                      />
                      <select
                        value={trigger.condition}
                        onChange={(e) => updateTrigger(idx, 'condition', e.target.value)}
                        className="input-field text-sm"
                      >
                        {TRIGGER_CONDITIONS.map((c) => (
                          <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={trigger.threshold ?? ''}
                        onChange={(e) => updateTrigger(idx, 'threshold', e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="Threshold"
                        className="input-field text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={trigger.description || ''}
                      onChange={(e) => updateTrigger(idx, 'description', e.target.value)}
                      placeholder="Description (e.g. Ferritin <30)"
                      className="input-field text-sm mt-2"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FormSection>

        {/* Safety */}
        <FormSection title="Safety" id="safety" expanded={expandedSections.has('safety')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Contraindications (comma-separated)" className="sm:col-span-2">
              <input
                type="text"
                value={product.contraindications.join(', ')}
                onChange={(e) => updateField('contraindications', parseArray(e.target.value))}
                placeholder="e.g. pregnancy, liver disease"
                className="input-field"
              />
            </Field>
            <Field label="Drug Interactions (comma-separated)" className="sm:col-span-2">
              <input
                type="text"
                value={product.drugInteractions.join(', ')}
                onChange={(e) => updateField('drugInteractions', parseArray(e.target.value))}
                placeholder="e.g. warfarin, lithium"
                className="input-field"
              />
            </Field>
            <Field label="Allergen Flags (comma-separated)" className="sm:col-span-2">
              <input
                type="text"
                value={product.allergenFlags.join(', ')}
                onChange={(e) => updateField('allergenFlags', parseArray(e.target.value))}
                placeholder="e.g. soy, shellfish"
                className="input-field"
              />
            </Field>
            <Field label="Pregnancy Category">
              <input
                type="text"
                value={product.pregnancyCategory || ''}
                onChange={(e) => updateField('pregnancyCategory', e.target.value)}
                placeholder="e.g. X, C, B"
                className="input-field"
              />
            </Field>
            <Field label="Monitoring Requirements (comma-separated)">
              <input
                type="text"
                value={product.monitoringRequirements.join(', ')}
                onChange={(e) => updateField('monitoringRequirements', parseArray(e.target.value))}
                placeholder="e.g. CBC every 3 months"
                className="input-field"
              />
            </Field>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={product.requiresRx}
                  onChange={(e) => updateField('requiresRx', e.target.checked)}
                  className="w-4 h-4 rounded border-bg-tertiary bg-bg-elevated text-accent focus:ring-accent"
                />
                <span className="text-base text-text-secondary">Requires Rx</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={product.requiresProvider}
                  onChange={(e) => updateField('requiresProvider', e.target.checked)}
                  className="w-4 h-4 rounded border-bg-tertiary bg-bg-elevated text-accent focus:ring-accent"
                />
                <span className="text-base text-text-secondary">Requires Provider</span>
              </label>
            </div>
          </div>
        </FormSection>

        {/* Evidence */}
        <FormSection title="Evidence" id="evidence" expanded={expandedSections.has('evidence')} onToggle={toggleSection}>
          <div className="space-y-4">
            <Field label="Evidence Level">
              <select value={product.evidenceLevel} onChange={(e) => updateField('evidenceLevel', e.target.value as 'strong' | 'moderate' | 'emerging')} className="input-field">
                {EVIDENCE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="Evidence Summary">
              <textarea
                value={product.evidenceSummary}
                onChange={(e) => updateField('evidenceSummary', e.target.value)}
                placeholder="Summary of clinical evidence..."
                rows={3}
                className="input-field"
              />
            </Field>
            <Field label="Citations (comma-separated)">
              <textarea
                value={product.citations.join(', ')}
                onChange={(e) => updateField('citations', parseArray(e.target.value))}
                placeholder="e.g. PMID: 12345678, DOI: 10.1234/example"
                rows={2}
                className="input-field"
              />
            </Field>
            <Field label="Clinical Notes">
              <textarea
                value={product.clinicalNotes}
                onChange={(e) => updateField('clinicalNotes', e.target.value)}
                placeholder="Internal clinical notes..."
                rows={3}
                className="input-field"
              />
            </Field>
            <Field label="Rationale Template">
              <textarea
                value={product.rationale}
                onChange={(e) => updateField('rationale', e.target.value)}
                placeholder="Template text for protocol output..."
                rows={3}
                className="input-field"
              />
            </Field>
          </div>
        </FormSection>

        {/* Demographics */}
        <FormSection title="Demographics" id="demographics" expanded={expandedSections.has('demographics')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Gender">
              <select value={product.gender} onChange={(e) => updateField('gender', e.target.value as 'female' | 'male' | 'any')} className="input-field">
                {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Min Age">
              <input
                type="number"
                value={product.ageMin ?? ''}
                onChange={(e) => updateField('ageMin', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="e.g. 18"
                className="input-field"
              />
            </Field>
            <Field label="Max Age">
              <input
                type="number"
                value={product.ageMax ?? ''}
                onChange={(e) => updateField('ageMax', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="e.g. 65"
                className="input-field"
              />
            </Field>
          </div>
        </FormSection>

        {/* Pricing */}
        <FormSection title="Pricing" id="pricing" expanded={expandedSections.has('pricing')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Manufacturing Cost">
              <input
                type="text"
                value={product.pricing.manufacturingCost || ''}
                onChange={(e) => updatePricing('manufacturingCost', e.target.value)}
                placeholder="e.g. $2.50-4.00"
                className="input-field"
              />
            </Field>
            <Field label="Supplier Cost">
              <input
                type="text"
                value={product.pricing.supplierCost || ''}
                onChange={(e) => updatePricing('supplierCost', e.target.value)}
                placeholder="e.g. $13-16/mo"
                className="input-field"
              />
            </Field>
            <Field label="Monthly Price">
              <input
                type="text"
                value={product.pricing.monthlyPrice || ''}
                onChange={(e) => updatePricing('monthlyPrice', e.target.value)}
                placeholder="e.g. $149/mo"
                className="input-field"
              />
            </Field>
          </div>
        </FormSection>

        {/* Meta */}
        <FormSection title="Meta" id="meta" expanded={expandedSections.has('meta')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Sort Order">
              <input
                type="number"
                value={product.sortOrder}
                onChange={(e) => updateField('sortOrder', Number(e.target.value))}
                className="input-field"
              />
            </Field>
            <Field label="Updated By">
              <input
                type="text"
                value={product.updatedBy}
                onChange={(e) => updateField('updatedBy', e.target.value)}
                placeholder="admin"
                className="input-field"
              />
            </Field>
            {product.updatedAt && (
              <Field label="Last Updated" className="sm:col-span-2">
                <p className="text-base text-text-muted py-2">{new Date(product.updatedAt).toLocaleString()}</p>
              </Field>
            )}
          </div>
        </FormSection>
      </div>

      {/* Bottom save bar */}
      <div className="sticky bottom-0 mt-6 py-4 bg-bg-deep border-t border-bg-tertiary flex items-center justify-end gap-3">
        <button
          onClick={() => navigate('/admin/products')}
          className="px-4 py-2.5 text-base text-text-secondary hover:text-text-primary transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save Product
        </button>
      </div>
    </div>
  );
}

// --- Reusable sub-components ---

function FormSection({
  title,
  id,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  id: string;
  expanded: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-bg-elevated transition-colors"
      >
        <h3 className="text-base font-semibold text-text-secondary uppercase tracking-wider">{title}</h3>
        {expanded ? <ChevronUp size={16} className="text-text-muted" /> : <ChevronDown size={16} className="text-text-muted" />}
      </button>
      {expanded && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-text-muted mb-1.5">
        {label}
        {required && <span className="text-accent ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
